import React, { useState, useRef, useEffect } from 'react';
import type {
  NavigationRailGroupProps,
  NavigationRailGroupMenuItem,
} from './NavigationRailGroup.types';
import { NavigationRailTypes } from '../NavigationRailTypes/NavigationRailTypes';
import { NavigationRailPanel } from '../NavigationRailPanel/NavigationRailPanel';
import { MagnifyingGlassIcon } from './icons';

/**
 * NavigationRailGroup del sistema de diseño Siesa
 *
 * Componente de navegación lateral agrupado que combina NavigationRailTypes
 * y NavigationRailPanel para crear una experiencia de navegación completa
 * con múltiples estados:
 *
 * - **Collapsed**: Barra compacta de 80px con solo iconos
 * - **Expanded**: Barra expandida de 215px con labels
 * - **Hover**: Panel lateral con submenú del item (se muestra al hacer hover)
 * - **Searcher**: Panel lateral con búsqueda y recientes
 *
 * **Características:**
 * - Reutiliza NavigationRailTypes para la barra lateral principal
 * - Reutiliza NavigationRailPanel para los paneles de hover y búsqueda
 * - Gestión de estado integrada (collapsed, expanded, hover, searcher)
 * - Dark mode completo en todos los elementos
 * - Focus rings adaptativos
 * - Transiciones suaves entre estados
 *
 * **Especificaciones de Figma:**
 * - NavigationRailTypes: 80px (collapsed) / 215px (expanded)
 * - NavigationRailPanel: 248px width, altura variable
 * - Posición panel: left 80px desde el rail
 * - Transiciones: 300ms ease-in-out
 *
 * **Estructura:**
 * ```
 * ┌────────────────────────────────────┐
 * │  NavigationRailGroup               │
 * │  ┌──────────┬─────────────────┐   │
 * │  │  Rail    │  Panel          │   │
 * │  │  Types   │  (hover/search) │   │
 * │  │  (base)  │  (opcional)     │   │
 * │  └──────────┴─────────────────┘   │
 * └────────────────────────────────────┘
 * ```
 *
 * **Estados de Items:**
 * - `default`: Sin interacción
 * - `hover`: Muestra panel con submenú (si tiene children)
 * - `active`: Item seleccionado, fondo custom-primary
 * - `disabled`: Opacity 50%, sin interacción
 *
 * **Colores (Light Mode):**
 * - Background rail: #ffffff (bg-primary)
 * - Background panel: #ffffff (bg-primary)
 * - Borders: #e4e4e7 (border-primary)
 * - Text: #18181b (content-primary)
 * - Icons: #a1a1aa (content-secondary)
 * - Search button: #0e79fd (primary-600)
 *
 * **Colores (Dark Mode):**
 * - Background rail: #18181b (dark-bg-primary)
 * - Background panel: #18181b (dark-bg-primary)
 * - Borders: #71717a (dark-border-primary)
 * - Text: #f4f4f5 (dark-content-primary)
 * - Icons: #a1a1aa (content-secondary)
 *
 * **Tipografía:**
 * - Items: Paragraph/Small (14px Regular, line-height 20px)
 * - Panel heading: Label/Small (14px Bold)
 * - Section headings: Paragraph/Tiny (12px Regular)
 *
 * **Implementación de Estados:**
 * - `collapsed` + hover en item → state='hover' + muestra panel
 * - `collapsed` + click en search → state='searcher' + muestra panel búsqueda
 * - `expanded` → NavigationRailTypes expandido con Input integrado
 * - Click en "Colapsar" → vuelve a collapsed
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico
 * @see docs/spacing.md - Sistema de espaciado
 * @see ../NavigationRailTypes - Componente reutilizado para barra lateral
 * @see ../NavigationRailPanel - Componente reutilizado para paneles
 * @see https://www.figma.com/design/5XNqf2YTxvwemxwo1LMQ6j/Siesa-UI-Kit?node-id=6146-34487
 *
 * @example
 * ```tsx
 * // Uso básico en modo collapsed
 * const items = [
 *   {
 *     id: 'admin',
 *     label: 'Administración',
 *     icon: <Cog6ToothIcon />,
 *     isCollapsible: true,
 *     children: [
 *       { id: 'users', label: 'Usuarios' },
 *       { id: 'roles', label: 'Roles' },
 *     ],
 *   },
 *   {
 *     id: 'sales',
 *     label: 'Ventas',
 *     icon: <ShoppingCartIcon />,
 *   },
 * ];
 *
 * <NavigationRailGroup
 *   state="collapsed"
 *   items={items}
 *   onItemClick={(item) => console.log('Clicked:', item.label)}
 *   onItemHover={(item) => console.log('Hover:', item.label)}
 * />
 *
 * // Uso con control de estado externo
 * const [state, setState] = useState('collapsed');
 *
 * <NavigationRailGroup
 *   state={state}
 *   items={items}
 *   onStateChange={setState}
 * />
 * ```
 *
 * @see NavigationRailGroup.types.ts - Props e interfaces
 */
export const NavigationRailGroup = React.forwardRef<
  HTMLDivElement,
  NavigationRailGroupProps
>(
  (
    {
      state = 'collapsed',
      items = [],
      onStateChange,
      onItemClick,
      onItemHover,
      labels = {},
      className,
      showSearchButton = true,
      onSearch,
      t = (key, def) => def || key,
    },
    ref
  ) => {
    // Estado local para controlar el panel
    const [showPanel, setShowPanel] = useState(false);
    const [panelType, setPanelType] = useState<'hover' | 'searcher'>('hover');
    const [hoveredItem, setHoveredItem] = useState<NavigationRailGroupMenuItem | null>(null);

    // Ref para manejar el timeout de ocultación del panel
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Fallbacks para labels si no se proveen
    const finalLabels = {
      searchPlaceholder: labels.searchPlaceholder ?? t('search', 'Buscar módulo'),
      collapseButton: labels.collapseButton ?? t('collapse', 'Colapsar'),
      recentSection: labels.recentSection ?? t('recent', 'Recientes'),
      frequentSection: labels.frequentSection ?? t('frequent', 'Opciones frecuentes'),
    };


    // Controlar el estado collapsed/expanded
    const isCollapsed = state === 'collapsed' || state === 'hover' || state === 'searcher';

    // Cleanup del timeout al desmontar el componente
    useEffect(() => {
      return () => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      };
    }, []);

    // Manejar cambio de estado collapsed/expanded
    const handleCollapsedChange = (collapsed: boolean) => {
      const newState = collapsed ? 'collapsed' : 'expanded';
      onStateChange?.(newState);
    };

    // Manejar click en item
    const handleItemClick = (index: number) => {
      const commercialItem = items[index];
      if (commercialItem) {
        onItemClick?.(commercialItem);
      }
    };

    // Manejar hover en item desde NavigationRailTypes
    const handleItemHoverFromRail = (index: number) => {
      const commercialItem = items[index];
      if (commercialItem && isCollapsed) {
        handleItemHover(commercialItem);
      }
    };

    // Manejar hover en item (solo en modo collapsed)
    const handleItemHover = (item: NavigationRailGroupMenuItem) => {
      if (isCollapsed && item.isCollapsible && item.children) {
        // Cancelar cualquier timeout pendiente
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }

        setHoveredItem(item);
        setPanelType('hover');
        setShowPanel(true);
        onItemHover?.(item);
      }
    };

    // Manejar cuando el cursor sale de un item
    const handleItemMouseLeave = () => {
      // Programar ocultación del panel con delay para permitir transición al panel
      if (isCollapsed && panelType === 'hover') {
        hideTimeoutRef.current = setTimeout(() => {
          setShowPanel(false);
          setHoveredItem(null);
        }, 150); // Delay de 150ms para permitir transición suave
      }
    };

    // Manejar cuando el cursor entra al panel
    const handlePanelMouseEnter = () => {
      // Cancelar la ocultación programada
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };

    // Manejar cuando el cursor sale del panel
    const handlePanelMouseLeave = () => {
      if (isCollapsed && panelType === 'hover') {
        setShowPanel(false);
        setHoveredItem(null);
      }
    };

    // Manejar click en botón de búsqueda


    // Convertir items a formato NavigationRailTypes
    const railItems = items.map((item) => ({
      id: item.id,
      label: item.label,
      icon: item.icon,
      active: item.active,
      disabled: item.disabled,
      badge: item.badge,
      badgeCount: item.badgeCount,
      children: item.children,
      defaultOpen: item.defaultOpen,
      onClick: () => {
        item.onClick?.();
      },
      onMouseLeave: () => handleItemMouseLeave(), // Corregir la llamada
    }));

    return (
      <div
        ref={ref}
        className={`relative flex items-start h-full ${className || ''}`}
        data-testid="navigation-rail-group"
      >
        {/* NavigationRailTypes - Barra lateral principal */}
        <NavigationRailTypes
          collapsed={isCollapsed}
          items={railItems}
          showRecentButton={false}
          showSearchButton={showSearchButton}
          onCollapsedChange={handleCollapsedChange}
          onItemClick={handleItemClick}
          onItemHover={handleItemHoverFromRail}
          onSearch={onSearch}
          labels={{
            searchButton: finalLabels.searchPlaceholder,
            searchPlaceholder: finalLabels.searchPlaceholder,
            collapseButton: finalLabels.collapseButton,
          }}
        />

        {/* NavigationRailPanel - Panel lateral (hover o searcher) */}
        {showPanel && isCollapsed && (
          <div
            className="absolute left-[80px] top-0 z-10 h-full"
            onMouseEnter={handlePanelMouseEnter}
            onMouseLeave={handlePanelMouseLeave}
          >
            <NavigationRailPanel
              height="100%"
              width={248}
              showHeading={panelType === 'hover' && hoveredItem !== null}
              heading={
                panelType === 'hover' && hoveredItem
                  ? {
                    label: hoveredItem.label,
                    iconName: 'cog-6-tooth',
                    showDescription: false,
                    showAction: false,
                  }
                  : undefined
              }
              showSearch={panelType === 'searcher'}
              search={{
                placeholder: finalLabels.searchPlaceholder,
              }}
              sections={
                panelType === 'hover' && hoveredItem?.children
                  ? [
                    {
                      items: hoveredItem.children.map((child) => ({
                        label: child.label,
                        icon: child.icon,
                        onClick: child.onClick,
                        disabled: child.disabled,
                      })),
                    },
                  ]
                  : panelType === 'searcher'
                    ? [
                      {
                        heading: finalLabels.recentSection,
                        items: [
                          {
                            label: 'Configuración',
                            icon: <MagnifyingGlassIcon className="size-4" />,
                          },
                        ],
                      },
                      {
                        heading: finalLabels.frequentSection,
                        items: [
                          {
                            label: 'Item',
                          },
                        ],
                      },
                    ]
                    : []
              }
            />
          </div>
        )}
      </div>
    );
  }
);

NavigationRailGroup.displayName = 'NavigationRailGroup';

export default NavigationRailGroup;
